import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { type VariantProps, cva } from "class-variance-authority";
import type { ComponentPropsWithoutRef, Ref } from "react";
import { cn } from "../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { Typography } from "./typography";
import { UploadFileButton, type ValidationError } from "./upload-button";

// Create a const assertion for better type safety
const AVATAR_SIZES = ["S", "M", "L", "XL"] as const;
type AvatarSize = (typeof AVATAR_SIZES)[number];

const avatarVariants = cva(
  "relative flex shrink-0 overflow-hidden rounded-full",
  {
    variants: {
      size: {
        S: "h-4 w-4",
        M: "h-6 w-6",
        L: "h-7 w-7",
        XL: "h-9 w-9",
      },
    },
    defaultVariants: {
      size: "XL",
    },
  },
);

const editButtonVariants = cva("absolute", {
  variants: {
    position: {
      S: "-right-2 -bottom-1",
      M: "-right-2 -bottom-1",
      L: "-right-2 -bottom-1",
      XL: "right-0.5 -bottom-0.5",
    },
  },
  defaultVariants: {
    position: "XL",
  },
});

const fallbackTextVariants = cva(
  "font-base text-gray-700 flex items-center justify-center h-full w-full",
  {
    variants: {
      size: {
        S: "text-avatar-xs",
        M: "text-avatar-sm",
        L: "text-avatar-md",
        XL: "text-avatar-lg",
      },
    },
    defaultVariants: {
      size: "XL",
    },
  },
);

interface AvatarProps
  extends ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  onEdit?: (file: File) => void;
  onValidationError?: (error: ValidationError) => void;
  showEditButton?: boolean;
  enableTooltip?: boolean;
  fullName?: string;
}

interface AvatarFallbackProps
  extends ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
  firstName?: string;
  lastName?: string;
  fallback?: string;
  size?: AvatarSize;
}

const Avatar = (props: AvatarProps & { ref?: Ref<HTMLDivElement> }) => {
  const {
    className,
    onEdit,
    onValidationError,
    showEditButton = false,
    enableTooltip = false,
    fullName,
    size = "XL",
    ...rest
  } = props;

  const avatarContent = (
    <div className="relative inline-block">
      <AvatarPrimitive.Root
        ref={props.ref}
        className={cn(avatarVariants({ size }), className)}
        {...rest}
      />
      {showEditButton && (
        <div className={editButtonVariants({ position: size })}>
          <UploadFileButton
            fileTypes={[".jpg", ".jpeg", ".png"]}
            icon="pencil"
            onFileSelect={onEdit}
            onValidationError={onValidationError}
            size="xs"
          />
        </div>
      )}
    </div>
  );

  if (enableTooltip && fullName) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{avatarContent}</TooltipTrigger>
        <TooltipContent>{fullName}</TooltipContent>
      </Tooltip>
    );
  }

  return avatarContent;
};
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = (
  props: ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & {
    ref?: Ref<HTMLImageElement>;
  },
) => {
  const { className, ...rest } = props;
  return (
    <AvatarPrimitive.Image
      ref={props.ref}
      className={cn("aspect-square h-full w-full object-cover", className)}
      {...rest}
    />
  );
};
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = (
  props: AvatarFallbackProps & {
    ref?: Ref<HTMLSpanElement>;
  },
) => {
  const {
    className,
    firstName,
    lastName,
    fallback = "AA",
    size = "XL",
    children,
    ...rest
  } = props as AvatarFallbackProps & { children?: React.ReactNode };
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || fallback;
  };

  return (
    <AvatarPrimitive.Fallback
      ref={props.ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-gray-50",
        className,
      )}
      {...rest}
    >
      {children || (
        <Typography className={cn(fallbackTextVariants({ size }))}>
          {getInitials(firstName, lastName)}
        </Typography>
      )}
    </AvatarPrimitive.Fallback>
  );
};
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback, type AvatarSize };
